import Header from "@components/Account/Layout/Header/Header";
import { PROFILE_ROUTES } from "@components/Account/Layout/utils";
import NotLoggedIn from "@components/Account/NotLoggedIn";
import LBox from "@components/LBox";
import LScreen from "@components/LScreen";
import LText from "@components/LText";
import { YouArtistsModalSWMSheet } from "@components/modals/YouArtistsModalSWM";
import { useGamificationSummary } from "@hooks/trpc/useGamificationSummary";
import { useMe } from "@hooks/trpc/useMe";
import { useShare } from "@hooks/trpc/useShare";
import { useTheme } from "@theme";
import { useAuth } from "@utils/firebaseAuth/auth";
import React, { useCallback } from "react";
import { ScrollView } from "react-native";
import { useBottomSheetManager } from "react-native-bottom-sheet-stack";
import Animated, {
  interpolateColor,
  useAnimatedStyle,
  useSharedValue,
} from "react-native-reanimated";
import { GamificationSummaryCards } from "./GamificationSummaryCards";
import { ProfileHeader } from "./ProfileHeader";
import { ProfileLoadingIndicator } from "./ProfileLoadingState";
import { TopArtistsList } from "./TopArtistsList";
import { SentryScreenDisplay } from "@components/SentryScreenDisplay";

export const YouScreen = () => {
  const theme = useTheme();
  const { status: authStatus } = useAuth();
  const isLoggedIn = authStatus === "logged-in";
  const summaryQuery = useGamificationSummary();
  const summary = summaryQuery.data ?? null;
  const { query: meQuery } = useMe();
  const user = meQuery.data;
  const userId = user?.id;
  const shareProfile = useShare();
  const bottomSheetManager = useBottomSheetManager();

  const pulse = useSharedValue(0);

  const handleShowAllArtists = useCallback(() => {
    bottomSheetManager.open(<YouArtistsModalSWMSheet />, {
      mode: "push",
    });
  }, [bottomSheetManager]);

  const handleShare = useCallback(async () => {
    if (!userId) return;

    await shareProfile.mutateAsync({
      linkType: "custom-action-link",
      type: "refer-friend",
      userId,
    });
  }, [shareProfile, userId]);

  const pulseStyle = useAnimatedStyle(() => {
    return {
      opacity: pulse.value * 0.18,
      backgroundColor: interpolateColor(
        pulse.value,
        [0, 1],
        ["rgba(0,0,0,0)", theme.colors.activeIndicatorLeft],
      ),
    };
  });

  return (
    <LScreen
      enableAutomaticTopPadding={true}
      enableAutomaticBottomPadding={!isLoggedIn}
      keyboard
      paddingHorizontal="zero"
      paddingBottom="l3"
    >
      <SentryScreenDisplay
        ready={!isLoggedIn || (!summaryQuery.isLoading && !meQuery.isLoading)}
      />
      {/* Visual pulse when stats update */}
      <Animated.View
        pointerEvents="none"
        style={[
          {
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
          },
          pulseStyle,
        ]}
      />

      <LBox pb="s4" px="s4">
        <Header routes={PROFILE_ROUTES} />
      </LBox>

      {!isLoggedIn ? (
        <LBox flex={1} alignItems="center" justifyContent="center">
          <NotLoggedIn
            flex={1}
            alignItems="center"
            justifyContent="center"
            width="70%"
            header="See your stats"
            text="Log in to view your stats."
          />
        </LBox>
      ) : (
        <ScrollView
          contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 120 }}
        >
          <LBox gap="s4">
            {summaryQuery.isLoading || meQuery.isLoading ? (
              <ProfileLoadingIndicator />
            ) : summary ? (
              <>
                {/* Profile Card - header only */}
                <LBox
                  padding="s3"
                  borderRadius="l"
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    borderWidth: 1,
                    borderColor: "rgba(255, 255, 255, 0.05)",
                  }}
                >
                  <ProfileHeader
                    userDisplayName={user?.name ?? "Lissen User"}
                    userAvatarUrl={user?.image ?? undefined}
                    defaultAvatarColors={user?.defaultAvatarColors}
                    onShare={userId ? handleShare : undefined}
                    isSharePending={shareProfile.isPending}
                  />
                </LBox>

                <GamificationSummaryCards
                  elo={summary.platform.elo}
                  tierLabel={summary.platform.rawTier.label}
                  spendableGems={summary.gems.spendableBalance}
                  recentGemDelta={summary.gems.recentEarnedDelta}
                  recentGemWindowDays={summary.gems.recentEarnedWindowDays}
                  onEloPress={handleShowAllArtists}
                />

                {/* Artists Card */}
                <LBox
                  padding="s4"
                  borderRadius="l"
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    borderWidth: 1,
                    borderColor: "rgba(255, 255, 255, 0.05)",
                  }}
                >
                  {summary.previewArtistStatuses.length > 0 ? (
                    <TopArtistsList
                      artists={summary.previewArtistStatuses}
                      showHeader={false}
                      maxVisible={3}
                      onShowAll={handleShowAllArtists}
                      currencySymbol={user?.plan?.currencySymbol ?? "£"}
                    />
                  ) : (
                    <LBox alignItems="center" py="s4" gap="s2">
                      <LText variant="Heading/H2/Bold" color="textPrimary">
                        Your Top Artists
                      </LText>
                      <LText
                        variant="Body/B2/Regular"
                        color="subText"
                        textAlign="center"
                      >
                        Start streaming to see your favorite artists here
                      </LText>
                    </LBox>
                  )}
                </LBox>
              </>
            ) : (
              <LBox
                flex={1}
                alignItems="center"
                justifyContent="center"
                py="l3"
              >
                <LText color="subText">No stats yet. Start streaming!</LText>
              </LBox>
            )}
          </LBox>
        </ScrollView>
      )}
    </LScreen>
  );
};
